import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { TFunction } from 'i18next'

import { ApiError, api } from '@/lib/api'
import { describeProblem, fieldErrors } from '@/lib/problems'
import { queryKeys } from '@/lib/query'
import { brandingRefusalFields, brandingRefusalMessage } from '@/lib/refusals'
import {
  MOCK_STATUS_SENDS,
  mockStatusEmailHtml,
  mockStatusEmailSubject,
} from '@/mocks/statusEmail'
import { USE_MOCKS } from '@/mocks/fixtures'
import type {
  BulkStatusEmailPreview,
  StatusEmailPreview,
  StatusSendDetail,
  StatusSendEntry,
} from '@/types/domain'

/* The client status email, before and after it is sent (S-15).
 *
 * BEFORE: the two send sheets show the exact email the send would mail — the
 * server's render, through the same composer and template the send uses, so
 * the preview and the outbox cannot disagree. A preview is a READ on the
 * server (no journal row, no outbox row, no bell, unlogged), refused exactly as
 * the matching send would be.
 *
 * AFTER: the send journal, one row per covered case per send, with the HTML and
 * subject exactly as they went out. It replaced the status PDF, which is no
 * longer rendered or filed; the PDFs filed before it still list under the
 * case's documents.
 *
 * Both HTML bodies are complete documents and are shown only through
 * `EmailFrame` (an empty `sandbox`), never with `dangerouslySetInnerHTML`.
 */

/* ── Preview ──────────────────────────────────────────────────────────────── */

/** Everything the server's render depends on that the sheet can change. */
export interface StatusPreviewInputs {
  /** The note AS THE SEND WILL CARRY IT — already debounced by the sheet, so
   *  the key does not move on every keystroke. */
  note: string
  language: 'en' | 'ru'
}

/** The per-case email as the send would mail it now.
 *
 *  `staleTime: 0`, against the client's 30 s default: a leg edited a moment
 *  ago must be in the preview the next time the sheet opens, not after half a
 *  minute. `keepPreviousData` keeps the last email in the frame while a new
 *  language or note renders, instead of flashing a skeleton per change.
 *
 *  `enabled` is the caller's "this sender may send": the sheet is open, there
 *  is a recipient, and `directory.contact.read` is held. A request the server
 *  would refuse 403 is not made. */
export function useCaseStatusPreview(
  caseId: string,
  inputs: StatusPreviewInputs,
  enabled: boolean,
) {
  return useQuery<StatusEmailPreview>({
    queryKey: queryKeys.cases.statusPreview(caseId, inputs.language, inputs.note),
    enabled: enabled && caseId !== '',
    staleTime: 0,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (USE_MOCKS) {
        return {
          subject: mockStatusEmailSubject(inputs.language, caseId),
          html: mockStatusEmailHtml(inputs.language),
          language: inputs.language,
        }
      }
      return api.post<StatusEmailPreview>(`/cases/${caseId}/status-update/preview`, {
        note: inputs.note,
        language: inputs.language,
      })
    },
  })
}

/** Which of the client's cases the consolidated email is about.
 *
 *  `'all'` is the sheet's untouched "every sendable case", sent as an empty
 *  `caseIds` — which the server reads as ALL of the client's cases, exactly as
 *  the send does. A list is an explicit pick; an EMPTY list is "nothing
 *  ticked", which must never reach the wire, where it would mean the opposite. */
export type ClientStatusSelection = 'all' | readonly string[]

/** The consolidated email as the send would mail it now.
 *
 *  Keyed on the SORTED ids, so ticking A then B and B then A is one cache
 *  entry, and disabled on an empty pick for the reason `ClientStatusSelection`
 *  states. The rest is `useCaseStatusPreview`'s. */
export function useClientStatusPreview(
  clientId: string,
  selection: ClientStatusSelection,
  inputs: StatusPreviewInputs,
  enabled: boolean,
) {
  const caseIds = selection === 'all' ? [] : [...selection].sort()
  const nothingTicked = selection !== 'all' && selection.length === 0

  return useQuery<BulkStatusEmailPreview>({
    queryKey: queryKeys.cases.clientStatusPreview(
      clientId,
      caseIds,
      inputs.language,
      inputs.note,
    ),
    enabled: enabled && clientId !== '' && !nothingTicked,
    staleTime: 0,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (USE_MOCKS) {
        return {
          subject: mockStatusEmailSubject(inputs.language, clientId),
          html: mockStatusEmailHtml(inputs.language),
          language: inputs.language,
          covered: caseIds.map((caseId) => ({ caseId, caseNumber: caseId })),
          skipped: [],
        }
      }
      return api.post<BulkStatusEmailPreview>('/cases/status-update/preview', {
        clientId,
        caseIds,
        note: inputs.note,
        language: inputs.language,
      })
    },
  })
}

/** A refused preview (or send) as the sentence the sheet shows in place of the
 *  email.
 *
 *  The preview is refused exactly as the matching send would be, so it is
 *  worded the way the pages word a refused send — with the one difference that
 *  nothing here is a retry of a write:
 *
 *    · FR-64's branding gap (422 `issuingEntity: branding-incomplete`) names
 *      the entity's missing fields through `lib/refusals`, the one reader of
 *      that wire shape;
 *    · a 403 is the missing `directory.contact.read` — who can send, never
 *      "check the connection";
 *    · 422 `clientContactEmail` is the client with no address on file;
 *    · anything else (the unwired renderer's 503, a network failure) is the
 *      problem → i18n mapping, never the server's English `detail`. */
export function statusRefusalMessage(error: unknown, t: TFunction): string {
  const branding = brandingRefusalFields(error)
  if (branding !== null) return brandingRefusalMessage(branding, t)
  if (error instanceof ApiError && error.isForbidden) {
    return t('cases.statusSend.noContactAccess')
  }
  if (fieldErrors(error)['clientContactEmail'] !== undefined) {
    return t('cases.statusSend.noContact')
  }
  return t(describeProblem(error).detailKey)
}

/* ── The send journal ─────────────────────────────────────────────────────── */

/** Every status email sent on this case, newest first.
 *
 *  Read through the case's own row scope on the server; a reader who reaches
 *  the case reaches its journal. `recipientEmail` is absent for a reader
 *  without `directory.contact.read`. Keyed under `cases.detail(id)`, so a send
 *  — which invalidates the case — lists its own row. */
export function useCaseStatusSends(caseId: string, enabled = true) {
  return useQuery<StatusSendEntry[]>({
    queryKey: queryKeys.cases.statusSends(caseId),
    enabled: enabled && caseId !== '',
    queryFn: async () => {
      if (USE_MOCKS) return MOCK_STATUS_SENDS
      return (await api.get<{ items: StatusSendEntry[] }>(`/cases/${caseId}/status-sends`))
        .items
    },
  })
}

/** One journal entry with its stored email, asked for only while it is open
 *  (`sendId` null otherwise). A sent email does not change, so the default
 *  staleness is fine here. */
export function useCaseStatusSend(caseId: string, sendId: string | null) {
  return useQuery<StatusSendDetail>({
    queryKey: queryKeys.cases.statusSend(caseId, sendId ?? ''),
    enabled: caseId !== '' && sendId !== null,
    queryFn: async () => {
      if (USE_MOCKS) {
        const entry = MOCK_STATUS_SENDS.find((one) => one.id === sendId)
        if (entry === undefined) throw new Error('unknown status send')
        return {
          ...entry,
          subject: mockStatusEmailSubject(entry.language, caseId),
          html: mockStatusEmailHtml(entry.language),
        }
      }
      return api.get<StatusSendDetail>(`/cases/${caseId}/status-sends/${sendId ?? ''}`)
    },
  })
}
